<template>
  <div class="plp">
    <div class="container">
      <AppBreadcrumbs />
      <div class="plp__head">
        <div class="plp__description">
          <h1 class="plp__heading heading heading--1">
            <span v-if="search">{{ $t('Search results for') }}: '{{ search }}'</span>
            <span v-else>{{ pageTitle }}</span>
          </h1>

          <UiInlineReadMore
            v-if="plpPage?.PLPData?.Description"
            class="plp__description--text"
          >
            <StrapiBlocksText :nodes="plpPage.PLPData?.Description" />
          </UiInlineReadMore>

          <FilterCategories
            v-if="!search"
            class="plp__filter-categories"
          />
        </div>
      </div>

      <div class="plp__container">
        <CatalogBanners
          class="plp__banners"
          :banners="plpBanners ?? []"
        />

        <div class="plp__toolbar">
          <PaginatorToolbar
            v-if="totalCount"
            class="plp-toolbar__totalcount"
            :total-count="totalCount"
            v-on="paginatorMethods"
          />
        </div>
        <PaginatorToolbar
          v-if="products?.length"
          class="plp__paginator plp__paginator--top"
          :sort="sort"
          :sort-options="sortOptions"
          v-on="paginatorMethods"
        />

        <div class="plp__sidebar">
          <FilterList
            class="plp__filter-list"
          />
        </div>
        <div
          v-if="products?.length"
          id="product-grid"
          class="plp__products products"
          :class="`products--${view}`"
        >
          <ProductTile
            v-for="(product, index) in products"
            :key="index"
            :product="product"
            :lazy="index > 2 || !plpBanners?.length"
            list="ProductListerPage"
          />

          <AppUsps
            v-if="plpUsps?.length && page === 1 && products.length >= 12"
            class="plp__usps"
            :usps="plpUsps"
          />

          <ProductBanner
            v-if="plpGridBanner1 && page === 1 && products.length >= 7"
            class="plp__banner plp__banner--1"
            :banner="plpGridBanner1"
          />

          <ProductBanner
            v-if="plpGridBanner2 && page === 1 && products.length >= 18"
            class="plp__banner plp__banner--2"
            :banner="plpGridBanner2"
          />
        </div>
        <div
          v-else
          class="plp__products products"
        >
          <p class="message message--warn no-products">
            <span>{{ $t('No products found') }}</span>
          </p>
        </div>

        <PaginatorToolbar
          v-if="products?.length"
          class="plp__paginator plp__paginator--bottom"
          :current-page="page"
          :total-pages="totalPages"
          :total-count="totalCount"
          v-on="paginatorMethods"
        />

        <UiReadMore
          v-if="plpPage?.PLPData?.SeoText"
          class="plp__seo--text"
        >
          <StrapiBlocksText
            class="plp__seo--strapi"
            :nodes="plpPage.PLPData?.SeoText"
          />
        </UiReadMore>
      </div>
    </div>

    <StrapiSlices
      v-if="plpPage?.Slices?.length"
      :slices="plpPage.Slices"
    />

    <div class="container">
      <ThirdpartySqueezelyProductSlider id="spotler-activate-plp-slider-1" />
      <ThirdpartySqueezelyProductSlider id="spotler-activate-plp-slider-2" />
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { DataLayerItem } from '~layers/app/composables/useDataLayer'

const { public: config } = useRuntimeConfig()
const { data: response } = await useAsyncGraphqlQuery('layout', {
  locale: config.strapiLocale,
}, useGraphqlDefaultClient())

const { setBreadcrumbs } = useBreadcrumbs()
const { search } = useCatalogSearch()
const route = useRoute()
const { t } = useI18n({ useScope: 'global' })

const meta = inject('meta', ref(search.value ? { strapi_id: response?.value?.data?.search?.[0]?.documentId, filters: [{ key: 'stock', values: ['in-stock'] }] } : undefined))

setBreadcrumbs({
  label: meta?.value?.label || 'Search',
  parents: meta?.value?.parents || [],
})

const {
  products,
  strapiData,
  fetchProductData,
  getStrapiData,
} = useCatalog(meta)

await Promise.all([
  fetchProductData(),
  meta?.value?.strapi_id ? getStrapiData() : undefined,
])

const plpPage = computed(() => {
  return strapiData.value?.plpPage
})

const plpUsps = computed(() => {
  return strapiData.value?.plpUsps?.[0].Usp
})

const plpTooltips = computed(() => {
  return strapiData.value?.tooltips
})

provide('tooltips', plpTooltips)

const plpBanners = computed(() => {
  return plpPage.value?.PLPData?.Banners
})

const plpGridBanner1 = computed(() => {
  return plpPage.value?.PLPData?.GridBanner1
})

const plpGridBanner2 = computed(() => {
  return plpPage.value?.PLPData?.GridBanner2
})

const {
  page,
  totalPages,
  sort,
  sortOptions,
  view,
  totalCount,
  paginatorMethods,
} = useCatalogPaginator(meta)

const pageTitle = computed(
  () => meta.value?.label,
)

const metaTitle = computed(
  () => plpPage.value?.MetaData?.MetaTitle || pageTitle.value,
)

const metaDescription = computed(
  () => plpPage.value?.MetaData?.MetaDescription || pageTitle.value + ' | ' + t('nuxtSiteConfig.description'),
)

const { pageView, viewItemList } = useDataLayer()
pageView('overview', pageTitle.value)

const dataLayerOverrides = computed<Partial<DataLayerItem>>(() => ({
  item_list_name: 'plp',
  item_list_id: route.fullPath,
}))

const category = computed<Partial<MagentoCategory>>(() => ({
  id: meta.value?.id?.split('-')?.[1],
  name: meta.value?.label,
}))

watch(products, () => {
  viewItemList(products.value, dataLayerOverrides.value, category.value)
}, { immediate: true })

const metaRobots = computed(() => {
  let index = 'index'
  let follow = 'follow'
  let strapiData = null

  const { p, filter, sort, color, ...restOfQuery } = route.query

  if (
    Object.keys(restOfQuery).length > 0
    || [filter, sort, p].some(param => param !== undefined)
  ) {
    index = 'noindex'
  }

  if (plpPage.value?.MetaData?.MetaRobots) {
    strapiData = plpPage.value.MetaData.MetaRobots.replace('_', ', ')
    if (strapiData.includes('noindex')) {
      index = 'noindex'
    }
    if (strapiData.includes('nofollow')) {
      follow = 'nofollow'
    }
  }

  return `${index}, ${follow}`
})

useHead({
  title: metaTitle,
  meta: [
    { hid: 'description', name: 'description', content: metaDescription },
    { hid: 'robots', name: 'robots', content: metaRobots },
  ],
})

const canonicalUrl = computed(() => {
  if (metaRobots.value.startsWith('index')) {
    return route.fullPath
  }
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { filter, color, ...restOfQuery } = route.query
  const searchParams = new URLSearchParams({
    ...(color ? { filter, color } : {}),
  })
  return route.path + (searchParams.size > 0 ? '?' + searchParams.toString() : '')
})

const { canonical } = useSeo()
canonical(canonicalUrl)
</script>

<style src="~layers/app/pages/~plp.css" />
